import React, { useEffect, useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import SkillInput, {
  APISkill,
  SkillData,
} from "@pages/hiringCompany/requirements/create/compoenents/skillInput";
import { getSkills, deleteSkill } from "@endpoints/skill.service";
import { JobSkills } from "@utils/constants";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import {
  updateJobRequirementSkill,
  addJobRequirementSkill,
} from "@endpoints/hiringCompany/requirements";

interface SkillsProps {
  createdDraftJob: { _id?: string };
  setValue: any;
}

const Skills: React.FC<SkillsProps> = ({ createdDraftJob, setValue }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [skillsList, setSkillsList] = useState<SkillData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setValue("skills", skillsList);
  }, [skillsList]);

  const fetchSkills = async () => {
    try {
      setIsLoading(true);
      const result = await getSkills({
        jobRequirementId: createdDraftJob?._id,
        type: 3,
      });
      if (result.success) {
        const mappedSkills = result.data.map(
          (skill: APISkill): SkillData => ({
            skill: skill.title,
            level: skill.rating.toString(),
            experience: skill.years.toString(),
            lastUsed:
              JobSkills.find((j) => j.value === skill.lastUsed)?.label || "",
            _id: skill?._id,
            isSaved: true,
            isEdit: false,
          })
        );
        setSkillsList(mappedSkills);
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch skills", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (createdDraftJob?._id) fetchSkills();
  }, [createdDraftJob?._id]);

  const addUpdateSkill = useCallback(
    async (data: SkillData) => {
      const lastUsedVal = JobSkills.find(
        (j) => j.label.toLowerCase() === data.lastUsed.toLowerCase()
      )?.value;

      const skillPayload = {
        title: data.skill,
        rating: Number(data.level),
        years: Number(data.experience),
        lastUsed: lastUsedVal,
        isWorkExperienceSummary: false,
      };

      try {
        const res = data?._id
          ? await updateJobRequirementSkill({ id: data?._id, ...skillPayload })
          : await addJobRequirementSkill({
              ...skillPayload,
              jobRequirementId: createdDraftJob?._id,
              type: 3,
              userType: 3,
            });

        if (res.success) {
          const updated = {
            ...data,
            _id: res.data?._id,
            isSaved: true,
            isEdit: false,
          };
          setSkillsList((prev) => [updated, ...prev]);
          return true;
        }
      } catch (err) {
        enqueueSnackbar("Failed to add skill", { variant: "error" });
      }
    },
    [createdDraftJob?._id]
  );

  const handleDelete = async (skill: SkillData) => {
    try {
      const res = await deleteSkill(skill?._id!);
      if (res.success) {
        setSkillsList((prev) => prev.filter((s) => s?._id !== skill?._id));
        enqueueSnackbar("Skill deleted", { variant: "success" });
      }
    } catch {
      enqueueSnackbar("Failed to delete skill", { variant: "error" });
    }
  };

  return (
    <div className="w-[80%] mx-auto">
      <p className="text-xs text-primary font-medium mb-2">Job skills</p>
      <SkillInput addUpdateSkill={addUpdateSkill} />

      {isLoading ? (
        <div className="flex justify-center mt-20">
          <LoadingAnimation color={colors.PrimaryBlack} />
        </div>
      ) : skillsList.length > 0 ? (
        <div className="mt-6">
          <div className="grid grid-cols-[1.9fr_0.75fr_0.6fr_1fr_0.5fr] font-semibold text-xs text-gray-700 px-20 mb-2">
            <span>Skill</span>
            <span>Rating</span>
            <span>Years</span>
            <span>When</span>
            <span></span>
          </div>
          <div className="space-y-4 px-20">
            {skillsList.map((s) => (
              <div
                key={s?._id}
                className="grid grid-cols-[1.7fr_0.68fr_0.58fr_1fr_0.5fr] items-center gap-x-3"
              >
                {[s.skill, `${s?.level}/10`, s.experience, s.lastUsed].map(
                  (val, idx) => (
                    <div
                      key={idx}
                      className="bg-[#F9FAFB] text-xs text-primary font-medium border border-[#ECECEC] rounded px-4 py-2.5"
                    >
                      {val}
                    </div>
                  )
                )}
                <button
                  className="text-xs font-medium text-[#6F6F6F]"
                  onClick={() => handleDelete(s)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Skills;
